import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

/*
  ================================================================================
  THEME & GENERAL STYLES
  ================================================================================
*/
export const colors = {
  primaryBg: 'var(--paper)',
  primaryText: 'var(--ink)',
  secondaryText: 'var(--muted)',
  accent: 'var(--ink)',
  accentLight: 'var(--ink)',
  highlight: 'var(--ink)',
  borderColor: 'var(--line)',
  cardBg: 'var(--surface)',
  cardBgStrong: 'var(--surface-strong)',
  cardHover: 'var(--surface-strong)',
  ctaBg: 'var(--surface)',
};

export const breakpoints = {
  mobile: '(max-width: 480px)',
  tablet: '(max-width: 768px)',
  desktop: '(min-width: 769px)',
};

/*
  ================================================================================
  LAYOUT COMPONENTS
  - Structured to be highly responsive, professional, and visually sharp
  ================================================================================
*/

/* 1) PageContainer: Wraps entire content */
export const PageContainer = styled.div`
  width: calc(100% - var(--gutter) * 2);
  max-width: 1248px;
  margin: 60px auto 80px;
  padding: 0;
  border-top: 1px solid var(--line);
`;

/* 2) HeroSection: Large, visually prominent top banner */
export const HeroSection = styled.section`
  max-width: var(--page-width);
  margin: 0 auto;
  color: var(--ink);
  background: var(--paper);
  padding: 160px var(--gutter) 48px;
  @media ${breakpoints.tablet} {
    padding-top: 130px;
  }
`;

/* 3) HeroTitle: Main hero heading */
export const HeroTitle = styled.h1`
  font-size: clamp(4.4rem, 6.4vw, 9rem);
  margin: 0 0 24px;
  line-height: 1.05;
  font-weight: 600;
  max-width: 1040px;
`;

/* 4) HeroSubtitle: Subheading */
export const HeroSubtitle = styled.p`
  font-size: 1.6rem;
  max-width: 760px;
  margin: 0;
  color: var(--muted);
  line-height: 1.7;

  @media ${breakpoints.tablet} {
    max-width: 100%;
    font-size: 1.6rem;
  }
`;

/* 5) ContentSection: Wrapper for each content block */
export const ContentSection = styled.section`
  width: 100%;
  margin: 4.5rem 0;

  @media ${breakpoints.mobile} {
    margin: 3rem 0;
  }
`;

/* 6) SectionTitle: Large heading for sections */
export const SectionTitle = styled.h2`
  font-size: clamp(2.2rem, 3.4vw, 3.4rem);
  margin-bottom: 1.4rem;
  color: var(--ink);
  text-align: left;
  font-weight: 500;

  @media ${breakpoints.tablet} {
    font-size: 2.2rem;
  }
`;

/* 7) SectionText: Paragraph text */
export const SectionText = styled.p`
  font-size: 1.6rem;
  color: var(--muted);
  margin-bottom: 1.2rem;
  line-height: 1.7;

  @media ${breakpoints.tablet} {
    font-size: 1.6rem;
  }
`;

/* 8) StyledList: Unordered list with custom bullet/hover effects */
export const StyledList = styled.ul`
  list-style-type: none;
  padding-left: 0;
  margin-top: 1rem;

  li {
    font-size: 1.6rem;
    margin-bottom: 0.75rem;
    position: relative;
    padding-left: 1.8rem;
    color: var(--muted);
    transition: all 0.3s ease;

    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: ${colors.accent};
      font-weight: bold;
    }

    &:hover {
      color: ${colors.highlight};
      transform: translateX(5px);
    }

    @media ${breakpoints.tablet} {
      font-size: 1.6rem;
    }
  }
`;

/* 9) QuoteContainer: Block for major quotes */
export const QuoteContainer = styled.div`
  width: 100%;
  background: var(--surface);
  border: 1px solid ${colors.borderColor};
  border-left: 4px solid ${colors.accent};
  border-radius: 1.6rem;
  padding: 2rem 2.4rem;
  margin: 3.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* 10) QuoteText: Larger, italic text with an animation */
export const QuoteText = styled(motion.blockquote)`
  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 1.6rem;
  color: var(--muted);
  font-style: italic;
  max-width: 1100px;
  margin: 0 auto;
  line-height: 1.6;
  text-align: center;

  @media ${breakpoints.tablet} {
    font-size: 1.6rem;
  }

  &::before {
    content: '“';
    font-size: 3rem;
    color: ${colors.accent};
    vertical-align: top;
    margin-right: 0.5rem;
  }

  &::after {
    content: '”';
    font-size: 3rem;
    color: ${colors.accent};
    vertical-align: bottom;
    margin-left: 0.5rem;
  }
`;

/* 11) SectionDivider: A thin horizontal line to break up sections */
export const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid var(--line);
  margin: 2.5rem 0;
  width: 100%;
`;

/* 12) ReferenceWrapper: Container for references at the bottom or inline */
export const ReferenceWrapper = styled.div`
  margin: 3rem 0;
  padding: 2rem 2.4rem;
  background: var(--surface);
  border: 1px solid ${colors.borderColor};
  border-radius: 1.2rem;

  h3 {
    font-size: 1.6rem;
    margin-bottom: 1rem;
    color: var(--ink);
  }

  p {
    font-size: 1.6rem;
    color: var(--muted);
    line-height: 1.6;
    margin-bottom: 1rem;
    display: flex;
    align-items: flex-start;

    span {
      font-weight: bold;
      margin-right: 0.6rem;
      color: var(--muted);
    }

    @media ${breakpoints.tablet} {
      font-size: 1.6rem;
    }
  }
`;

/* 13) Footer: Optional page footer */
export const PageFooter = styled.footer`
  text-align: center;
  margin: 2rem 0;
  font-size: 1.6rem;
  padding: 1rem;
  padding-top: 0;
  color: var(--muted);

  @media ${breakpoints.tablet} {
    font-size: 1.6rem;
  }
`;

export const CalloutCard = styled.div`
  margin: 0 auto 2.5rem;
  width: calc(100% - var(--gutter) * 2);
  max-width: 1248px;
  padding: 2.2rem 2.6rem;
  background: var(--surface);
  border-radius: 1.8rem;
  border: 1px solid ${colors.borderColor};
  box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);

  @media ${breakpoints.tablet} {
    padding: 1.8rem;
    margin: 0 auto 2rem;
  }
`;

export const CalloutTitle = styled.h3`
  font-size: 2rem;
  color: var(--ink);
  margin-bottom: 0.9rem;

  @media ${breakpoints.tablet} {
    font-size: 1.6rem;
  }
`;

export const CalloutText = styled.p`
  font-size: 1.6rem;
  color: var(--muted);
  line-height: 1.7;
  margin-bottom: 1.6rem;
`;

export const CalloutButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const CalloutLink = styled(Link)`
  padding: 0.9rem 1.6rem;
  border-radius: 999px;
  text-decoration: none;
  font-weight: 600;
  color: var(--ink);
  background: var(--surface);
  border: 1px solid var(--line);
  font-size: 1.6rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  &:hover {
    color: var(--ink);
    box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
    transform: translateY(-1px);
  }
`;
